import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Trash2, Plus, GripVertical, Circle, CheckSquare, MessageSquareText } from 'lucide-react';
import type { PollQuestion } from '../types';

interface PollQuestionFormProps {
  question: PollQuestion;
  index: number;
  onChange: (updated: PollQuestion) => void;
  onRemove: () => void;
}

const QUESTION_TYPES = [
  { value: 'single_choice', label: 'Single Choice', icon: Circle },
  { value: 'multiple_choice', label: 'Multiple Choice', icon: CheckSquare },
  { value: 'free_text', label: 'Free Text', icon: MessageSquareText },
];

export function PollQuestionForm({ question, index, onChange, onRemove }: PollQuestionFormProps) {
  const isChoiceType = question.type === 'single_choice' || question.type === 'multiple_choice';

  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-3">
          <div className="flex items-center gap-1 pt-2.5 text-muted-foreground/40">
            <GripVertical className="h-4 w-4" />
            <span className="text-xs font-medium min-w-[1.25rem] text-center">{index + 1}</span>
          </div>

          <div className="flex-1 space-y-3">
            <Input
              value={question.text}
              onChange={(e) => onChange({ ...question, text: e.target.value })}
              placeholder="Enter your question..."
              className="font-medium border-0 border-b rounded-none px-0 shadow-none focus-visible:ring-0 focus-visible:border-primary"
            />

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <div className="flex flex-wrap gap-1">
                {QUESTION_TYPES.map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => {
                        const updated = { ...question, type: t.value };
                        if (
                          (t.value === 'single_choice' || t.value === 'multiple_choice') &&
                          !question.options?.length
                        ) {
                          updated.options = ['', ''];
                        } else if (t.value === 'free_text') {
                          updated.options = undefined;
                        }
                        onChange(updated);
                      }}
                      className={`
                        flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-colors
                        ${
                          question.type === t.value
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                        }
                      `}
                    >
                      <Icon className="h-3 w-3" />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id={`q-required-${question.questionId}`}
                  checked={question.required}
                  onCheckedChange={(checked) =>
                    onChange({ ...question, required: checked === true })
                  }
                />
                <Label
                  htmlFor={`q-required-${question.questionId}`}
                  className="text-xs text-muted-foreground"
                >
                  Required
                </Label>
              </div>
            </div>

            {isChoiceType && (
              <div className="space-y-2 pl-1">
                {(question.options || []).map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-muted-foreground/40">
                      {question.type === 'single_choice' ? (
                        <Circle className="h-3.5 w-3.5" />
                      ) : (
                        <CheckSquare className="h-3.5 w-3.5" />
                      )}
                    </span>
                    <Input
                      value={opt}
                      onChange={(e) => {
                        const newOptions = [...(question.options || [])];
                        newOptions[i] = e.target.value;
                        onChange({ ...question, options: newOptions });
                      }}
                      placeholder={`Option ${i + 1}`}
                      className="h-8 text-sm"
                    />
                    {(question.options?.length ?? 0) > 2 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground/50 hover:text-destructive"
                        onClick={() => {
                          const newOptions = (question.options || []).filter((_, j) => j !== i);
                          onChange({ ...question, options: newOptions });
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-muted-foreground"
                  onClick={() => {
                    onChange({ ...question, options: [...(question.options || []), ''] });
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add option
                </Button>
              </div>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground/50 hover:text-destructive mt-1"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
